import { useEffect, useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLinkedin, faGithub } from '@fortawesome/free-brands-svg-icons';
import { faArrowDown } from '@fortawesome/free-solid-svg-icons';

import resumePdf from '../../assets/Darshan_Lal_CV.pdf';
import { siteConfig } from '../../config/site';
import { MAIN_NAV_LINKS } from '../../config/navigation';
import useActiveSection from '../../hooks/useActiveSection';

import '../../styles/Header/Header.scss';

const SECTION_IDS = MAIN_NAV_LINKS.map((l) => l.href.slice(1));

const Header = () => {
  const [navOpen, setNavOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const active = useActiveSection(SECTION_IDS);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.classList.toggle('is-locked', navOpen);
    if (!navOpen) return undefined;
    const onKey = (e) => e.key === 'Escape' && setNavOpen(false);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.classList.remove('is-locked');
    };
  }, [navOpen]);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia('(min-width: 901px)');
    if (!mq || typeof mq.addEventListener !== 'function') return undefined;
    const onChange = () => mq.matches && setNavOpen(false);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const closeNav = () => setNavOpen(false);
  const [first] = siteConfig.displayName.split(' ');

  return (
    <header className={`header${scrolled ? ' header--scrolled' : ''}${navOpen ? ' header--open' : ''}`}>
      <div className="header__bar">
        <a href="#top" className="header__logo" aria-label={`${siteConfig.displayName}'s portfolio, back to top`} onClick={closeNav}>
          <span className="header__logo-mark" aria-hidden="true">
            DL
          </span>
          <span className="header__logo-text">
            {first}
            <span className="header__logo-dot">.</span>
          </span>
        </a>

        <nav className="header__nav" aria-label="Primary">
          <ul>
            {MAIN_NAV_LINKS.map((item) => (
              <li key={item.href}>
                <a
                  href={item.href}
                  className={active === item.href.slice(1) ? 'is-active' : undefined}
                  aria-current={active === item.href.slice(1) ? 'true' : undefined}
                >
                  {item.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className="header__actions">
          <a className="btn btn--primary btn--sm header__resume" href={resumePdf} download={siteConfig.resumeDownloadFilename}>
            Résumé <FontAwesomeIcon icon={faArrowDown} aria-hidden="true" />
          </a>
          <button
            type="button"
            className="header__burger"
            aria-expanded={navOpen}
            aria-controls="mobile-menu"
            aria-label={navOpen ? 'Close navigation menu' : 'Open navigation menu'}
            onClick={() => setNavOpen((o) => !o)}
          >
            <span />
            <span />
          </button>
        </div>
      </div>

      <div className="header__mobile" id="mobile-menu" hidden={!navOpen}>
        <ul>
          {MAIN_NAV_LINKS.map((item, i) => (
            <li key={item.href} style={{ '--i': i }}>
              <a href={item.href} onClick={closeNav}>
                <span className="header__mobile-index">0{i + 1}</span>
                {item.label}
              </a>
            </li>
          ))}
        </ul>
        <div className="header__mobile-footer">
          <a className="btn btn--primary" href={resumePdf} download={siteConfig.resumeDownloadFilename} onClick={closeNav}>
            Download résumé <FontAwesomeIcon icon={faArrowDown} aria-hidden="true" />
          </a>
          <div className="header__mobile-social">
            <a href={siteConfig.social.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
              <FontAwesomeIcon icon={faLinkedin} />
            </a>
            <a href={siteConfig.social.github} target="_blank" rel="noopener noreferrer" aria-label="GitHub">
              <FontAwesomeIcon icon={faGithub} />
            </a>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
